import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Navigate, Route } from 'react-router-dom';

import 'intro.js/introjs.css';
import 'react-datepicker/dist/react-datepicker.css';

import { EnvVars } from '../environment';
import { UserProvider } from '@/Contexts/User';
import { ErrorBoundary } from '@/new/components/error-boundary';
import { Icon } from '@/new/components/icon';
import { RelativityCallback } from '@/new/components/oauth/RelativityCallback';
import { TooltipProvider } from '@/new/components/shadcn/tooltip';
import { Toaster } from '@/new/components/toaster';
import { Admin } from '@/new/pages/admin/organisations';
import { AdminManageOrganisation } from '@/new/pages/admin/organisations/:organisationId';
import { OrganisationRuns } from '@/new/pages/admin/organisations/:organisationId/runs';
import { AdminManageUsers } from '@/new/pages/admin/organisations/:organisationId/users';
import { ClientMaterials } from '@/new/pages/client-materials';
import { Guide } from '@/new/pages/guide';
import { Layout } from '@/new/pages/layout';
import { ManageUsers } from '@/new/pages/manage-users';
import { Matters } from '@/new/pages/matters';
import { MatterOverview } from '@/new/pages/matters/:matterId';
import { DocumentUpload } from '@/new/pages/matters/:matterId/docs/upload';
import { MatterSettings } from '@/new/pages/matters/:matterId/settings';
import { Subsets } from '@/new/pages/matters/:matterId/subsets';
import { SubsetOverview } from '@/new/pages/matters/:matterId/subsets/:subsetId';
import { Chronologies } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies';
import { Chronology } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/:chronologyId';
import { Documents } from '@/new/pages/matters/:matterId/subsets/:subsetId/docs';
import { Document } from '@/new/pages/matters/:matterId/subsets/:subsetId/docs/:docId';
import { SubsetEditor } from '@/new/pages/matters/:matterId/subsets/:subsetId/edit';
import { Entities } from '@/new/pages/matters/:matterId/subsets/:subsetId/entities';
import { Facts } from '@/new/pages/matters/:matterId/subsets/:subsetId/facts';
import { Inconsistencies } from '@/new/pages/matters/:matterId/subsets/:subsetId/inconsistencies';
import { Kim } from '@/new/pages/matters/:matterId/subsets/:subsetId/kim';
import { KimThread } from '@/new/pages/matters/:matterId/subsets/:subsetId/kim/:threadId';
import { Realtime } from '@/new/pages/matters/:matterId/subsets/:subsetId/realtime';
import { Transcript } from '@/new/pages/matters/:matterId/subsets/:subsetId/realtime/:transcriptId';
import { Reports } from '@/new/pages/matters/:matterId/subsets/:subsetId/reports';
import { Report } from '@/new/pages/matters/:matterId/subsets/:subsetId/reports/:reportId';
import { SubsetCreator } from '@/new/pages/matters/:matterId/subsets/new';
import { MatterCreator } from '@/new/pages/matters/new';
import { OrganisationSettings } from '@/new/pages/organisation-settings';
import { FieldMappingsPage } from '@/new/pages/relativity/field-mappings';
import { ReleaseNotes } from '@/new/pages/release-notes';
import { UserSettings } from '@/new/pages/settings';
import { Usage } from '@/new/pages/usage';

const queryClient = new QueryClient();

function App() {
  return (
    <ErrorBoundary>
      <RequiredAuthProvider
        authUrl={EnvVars.AUTH_URL}
        displayWhileLoading={
          <div className="flex h-screen w-screen items-center justify-center">
            <Icon className="lucide--loader-circle animate-spin text-gray-500" />
          </div>
        }
        displayIfLoggedOut={<RedirectToLogin />}
      >
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <BrowserRouter>
              <TooltipProvider>
                <Toaster />
                <Routes>
                  <Route path="oauth/relativity/callback" element={<RelativityCallback />} />

                  {/* New frontend */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Navigate to="/matters" replace />} />

                    <Route path="settings" element={<UserSettings />} />
                    <Route path="guide" element={<Guide />} />

                    <Route path="client-materials" element={<ClientMaterials />} />
                    <Route path="release-notes" element={<ReleaseNotes />} />

                    <Route path="usage" element={<Usage />} />
                    <Route path="manage-users" element={<ManageUsers />} />

                    <Route path="organisation-settings/*" element={<OrganisationSettings />} />
                    <Route path="relativity/field-mappings" element={<FieldMappingsPage />} />

                    <Route path="admin">
                      <Route path="organisations">
                        <Route index element={<Admin />} />
                        <Route path=":organisationId">
                          <Route index element={<AdminManageOrganisation />} />
                          <Route path="users" element={<AdminManageUsers />} />
                          <Route path="runs" element={<OrganisationRuns />} />
                        </Route>
                      </Route>
                    </Route>

                    <Route path="matters">
                      <Route index element={<Matters />} />
                      <Route path="new/:matterId" element={<MatterCreator />} />
                      <Route path=":matterId">
                        <Route index element={<MatterOverview />} />
                        <Route path="docs">
                          <Route path="upload" element={<DocumentUpload />} />
                        </Route>

                        <Route path="settings" element={<MatterSettings />}>
                          <Route path="metadata" />
                          <Route path="issues" />
                          <Route path="document-management" />
                          <Route path="users" />
                          <Route path="run-history">
                            <Route path=":runId" />
                          </Route>
                          <Route path="upload-history">
                            <Route path=":uploadId" />
                          </Route>
                        </Route>

                        <Route path="subsets">
                          <Route index element={<Subsets />} />
                          <Route path="new" element={<SubsetCreator />} />

                          <Route path=":subsetId">
                            <Route path="edit" element={<SubsetEditor />} />
                            <Route index element={<SubsetOverview />} />
                            <Route path="facts" element={<Facts />} />
                            <Route path="chronologies">
                              <Route index element={<Chronologies />} />
                              <Route path=":chronologyId" element={<Chronology />} />
                            </Route>

                            <Route path="docs">
                              <Route index element={<Documents />} />
                              <Route path=":docId" element={<Document />} />
                            </Route>
                            <Route path="inconsistencies" element={<Inconsistencies />} />
                            <Route path="reports">
                              <Route index element={<Reports />} />
                              <Route path=":reportId" element={<Report />} />
                            </Route>
                            <Route path="kim">
                              <Route index element={<Kim />} />
                              <Route path=":threadId" element={<KimThread />} />
                            </Route>
                            <Route path="realtime">
                              <Route index element={<Realtime />} />
                              <Route path=":transcriptId" element={<Transcript />} />
                            </Route>
                            <Route path="entities" element={<Entities />} />
                          </Route>
                        </Route>
                      </Route>
                    </Route>
                  </Route>
                </Routes>
              </TooltipProvider>
            </BrowserRouter>
          </UserProvider>
        </QueryClientProvider>
      </RequiredAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
